<template>
  <div class="typing-game" :class="{ collapsed: isStatsCollapsed }">
    <div
      class="game-sidebar"
      v-if="gameStarted"
      :class="{ collapsed: isStatsCollapsed }"
    >
      <div class="collapse-toggle" @click="toggleStats">
        <span class="toggle-icon">{{ isStatsCollapsed ? "◀" : "▶" }}</span>
      </div>

      <div class="stats-container">
        <div class="sidebar-header">
          <h1>{{ title }}</h1>
          <div class="divider"></div>
        </div>

        <div class="stat-box score">
          <div class="stat-icon">🎯</div>
          <div class="stat-content">
            <div class="stat-value">{{ score }}</div>
            <div class="stat-label">得分</div>
          </div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">✨</div>
          <div class="stat-content">
            <div class="stat-value">{{ accuracy }}%</div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
        <div class="stat-box speed">
          <div class="stat-icon">⚡</div>
          <div class="stat-content">
            <div class="stat-value">{{ typingSpeed }}</div>
            <div class="stat-label">字/分钟</div>
          </div>
        </div>
        <div class="divider"></div>
        <button class="exit-button" @click="exitPractice">
          <span class="button-icon">🚪</span>
          退出练习
        </button>
      </div>
    </div>

    <div class="game-main">
      <div class="game-display" :class="{ correct: isCorrect, wrong: isWrong }">
        <div class="target-letters">
          <span
            v-for="(letter, index) in targetLetters"
            :key="index"
            :class="{
              current: index === currentIndex,
              completed: index < currentIndex && !errorIndices.includes(index),
              'error-marked': errorIndices.includes(index),
              upcoming: index > currentIndex,
              space: letter === ' ',
              error: index === currentIndex && isWrong,
            }"
            >{{ letter === " " ? "␣" : letter }}</span
          >
        </div>
        <div
          class="letter-instruction"
          style="text-align: center; margin: 15px auto; max-width: 500px;"
        >
          <template v-if="isWrong">
            <div class="error-feedback">
              <div class="error-icon">❌</div>
              <div class="error-message">
                <span class="error-title">哎呀！出错啦</span>
                <span class="error-detail">
                  应输入 <span class="key-highlight correct">{{ currentLetter === " " ? "空格键" : currentLetter }}</span>，
                  您输入了 <span class="key-highlight wrong">{{ lastWrongKey === " " ? "空格键" : lastWrongKey }}</span>
                </span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="instruction-bubble">
              <div class="instruction-icon">{{ currentLetter === " " ? "👣" : "👆" }}</div>
              <div class="instruction-text">
                {{ currentLetter === " " ? "请按空格键" : `请输入${currentLetter}` }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="progress-container">
        <div class="progress">
          <div class="progress-bar" :style="{ width: progress + '%' }">
            <div class="progress-glow"></div>
          </div>
        </div>
        <div class="progress-text">
          <span class="progress-label">练习进度</span>
          <span class="progress-value">{{ Math.round(progress) }}%</span>
        </div>
      </div>

      <div class="practice-keyboard">
        <div class="practice-row">
          <div
            v-for="key in [
              '`',
              '1',
              '2',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              '9',
              '0',
              '-',
              '=',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key backspace-key">←</div>
        </div>
        <div class="practice-row">
          <div class="practice-key tab-key">Tab</div>
          <div
            v-for="key in [
              'Q',
              'W',
              'E',
              'R',
              'T',
              'Y',
              'U',
              'I',
              'O',
              'P',
              '[',
              ']',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key backslash-key">\</div>
        </div>
        <div class="practice-row">
          <div class="practice-key caps-key">Caps</div>
          <div
            v-for="key in [
              'A',
              'S',
              'D',
              'F',
              'G',
              'H',
              'J',
              'K',
              'L',
              ';',
              '\'',
            ]"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key enter-key">Enter</div>
        </div>
        <div class="practice-row">
          <div class="practice-key shift-key">Shift</div>
          <div
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/']"
            :key="key"
            class="practice-key"
            :class="{ active: currentLetter.toUpperCase() === key }"
          >
            {{ key }}
          </div>
          <div class="practice-key shift-key-right">Shift</div>
        </div>
        <div class="practice-row">
          <div class="practice-key ctrl-key">Ctrl</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key alt-key">Alt</div>
          <div
            class="practice-key space-key"
            :class="{ active: currentLetter === ' ' }"
          >
            空格
          </div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key menu-key">≡</div>
          <div class="practice-key ctrl-key">Ctrl</div>
        </div>
      </div>
    </div>

    <!-- 引导页面 -->
    <div class="guide-modal" v-if="!gameStarted">
      <div class="guide-content">
        <div class="guide-header">
          <h2>{{ title }}</h2>
          <p class="guide-subtitle">挑战{{ practiceLength }}个字符的组合输入</p>
        </div>

        <div class="guide-body">
          <div class="guide-section">
            <h3>🎯 练习目标</h3>
            <ul>
              <li>完成{{ practiceLength }}个字符的连续输入</li>
              <li>
                熟练掌握{{
                  practiceKeys
                    .map((k) => (k === " " ? "空格键" : k.toUpperCase()))
                    .join("、")
                }}的组合
              </li>
              <li>提高输入准确度和速度</li>
            </ul>
          </div>

          <div class="guide-section">
            <h3>💡 练习要点</h3>
            <ul>
              <li v-for="(key, index) in practiceKeys" :key="index">
                {{
                  key === " "
                    ? "大拇指自然放在空格键上"
                    : index % 2 === 0
                    ? `左手食指放在${key.toUpperCase()}键上`
                    : `右手食指放在${key.toUpperCase()}键上`
                }}
              </li>
              <li>保持正确的手指位置不变</li>
            </ul>
          </div>

          <div class="keyboard-preview">
            <div class="preview-row">
              <div
                v-for="(key, index) in practiceKeys.filter((k) => k !== ' ')"
                :key="index"
                class="preview-key"
              >
                {{ key.toUpperCase() }}
              </div>
            </div>
            <div class="preview-row" v-if="practiceKeys.includes(' ')">
              <div class="preview-key space">Space</div>
            </div>
          </div>
        </div>

        <div class="guide-footer">
          <button @click="startGame" class="start-button">
            <span class="button-icon">🎮</span>
            开始练习
          </button>
          <button @click="backToMap" class="back-button">
            <span class="button-icon">🗺️</span>
            返回
          </button>
        </div>
      </div>
    </div>

    <!-- 添加打字报告弹窗 -->
    <div class="report-modal" v-if="showReport">
      <div class="report-content">
        <h2>{{ title }}练习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>

        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage" />
            <p>{{ resultMessage }}</p>
          </div>
        </div>

        <div class="report-buttons">
          <button @click="restartPractice">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TypingPlanetIntroPractice",
  data() {
    return {
      title: "综合练习",
      gameStarted: true,
      showGuide: false,
      isStatsCollapsed: false,
      currentLetter: "",
      targetLetters: "",
      currentIndex: 0,
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      progress: 0,
      isCorrect: false,
      isWrong: false,
      showReport: false,
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: "",
      resultImage: "",
      resultImages: {
        excellent: "https://wugecdn.steam.fun/typefun/images/excellent.png",
        good: "https://wugecdn.steam.fun/typefun/images/good.png",
        keepTrying: "https://wugecdn.steam.fun/typefun/images/keep-trying.png",
      },
      practiceKeys: ["r", "u", " "],
      practiceLength: 50,
      levelId: "intro24",
      nextLevel: "/intro/level5",
      inputBuffer: [],
      isProcessingInput: false,
      lastKeyTime: 0,
      errorIndices: [],
      lastWrongKey: "",
    };
  },
  methods: {
    startGame() {
      this.gameStarted = true;
      this.showGuide = false;
      this.startTime = new Date();
      this.generateChallenge();
      window.addEventListener("keydown", this.handleKeyPress);
    },

    generateChallenge() {
      let challenge = "";
      for (let i = 0; i < this.practiceLength; i++) {
        const randomChar = this.practiceKeys[
          Math.floor(Math.random() * this.practiceKeys.length)
        ];
        challenge += randomChar;
      }
      this.targetLetters = challenge;
      this.currentLetter = this.targetLetters[0];
      this.currentIndex = 0;
    },

    handleKeyPress(event) {
      if (event.code === "Space") {
        event.preventDefault();
      }

      if (!this.gameStarted) return;
      
      // 如果当前处于错误状态，不接受任何输入
      if (this.isWrong) return;

      // 阻止Tab和Escape键的默认行为
      if (
        event.key === "Tab" ||
        event.key === "Escape" ||
        event.key === "Backspace" ||
        event.key === "Delete"
      ) {
        event.preventDefault();
      }

      // 如果是大小写切换键或其他控制键，直接忽略，不计入错误
      if (
        event.key === "Shift" ||
        event.key === "CapsLock" ||
        event.key === "Control" ||
        event.key === "Alt" ||
        event.key === "Meta" ||
        event.key === "AltGraph" ||
        event.key === "ContextMenu" ||
        event.key === "OS" ||
        event.key === "Fn" ||
        event.key === "Tab" ||
        event.key === "Escape" ||
        event.key === "Backspace" ||
        event.key === "Delete" ||
        event.key.indexOf("Arrow") === 0 ||
        event.key === "Home" ||
        event.key === "End" ||
        event.key === "PageUp" ||
        event.key === "PageDown"
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      // 记录按键时间，用于计算打字速度
      const currentTime = new Date().getTime();
      this.lastKeyTime = currentTime;

      // 将按键添加到输入缓冲区
      this.inputBuffer.push(key);

      // 如果当前没有在处理输入，则开始处理
      if (!this.isProcessingInput) {
        this.processInputBuffer();
      }
    },

    processInputBuffer() {
      // 如果缓冲区为空，结束处理
      if (this.inputBuffer.length === 0) {
        this.isProcessingInput = false;
        return;
      }

      this.isProcessingInput = true;

      // 从缓冲区取出一个按键
      const key = this.inputBuffer.shift();
      this.totalAttempts++;

      if (key === this.currentLetter.toLowerCase()) {
        this.handleCorrectKey();
      } else {
        this.lastWrongKey = key; // 记录错误的键
        this.handleWrongKey();
      }

      this.updateAccuracy();

      // 继续处理缓冲区中的下一个按键
      if (this.inputBuffer.length > 0 && !this.showReport) {
        // 使用requestAnimationFrame确保UI更新后再处理下一个输入
        requestAnimationFrame(() => this.processInputBuffer());
      } else {
        this.isProcessingInput = false;
      }
    },

    handleCorrectKey() {
      this.isCorrect = true;
      this.score += 10;
      this.correctAttempts++;
      this.accuracy = Math.round(
        (this.correctAttempts / this.totalAttempts) * 100
      );
      this.progress = Math.round(
        ((this.currentIndex + 1) / this.targetLetters.length) * 100
      );

      // 播放音效但不阻塞进程
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/correct.mp3"
      );
      audio.volume = 0.5; // 降低音量以减少快速输入时的噪音
      audio.play().catch((e) => console.log("音频播放失败:", e));

      if (this.currentIndex === this.targetLetters.length - 1) {
        this.generateReport();
        return;
      }

      // 立即更新到下一个字符，不再使用setTimeout
      this.currentIndex++;
      this.currentLetter = this.targetLetters[this.currentIndex];

      // 使用短暂的CSS动画效果而不是延迟
      setTimeout(() => {
        this.isCorrect = false;
      }, 100); // 减少为100ms，只是为了视觉反馈
    },

    handleWrongKey() {
      this.isWrong = true;
      this.score = Math.max(0, this.score - 5);
      this.accuracy = Math.round(
        (this.correctAttempts / this.totalAttempts) * 100
      );

      // 记录错误的索引
      this.errorIndices.push(this.currentIndex);

      // 播放音效但不阻塞进程
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/fail.mp3"
      );
      audio.volume = 0.5; // 降低音量以减少快速输入时的噪音
      audio.play().catch((e) => console.log("音频播放失败:", e));

      // 清空输入缓冲区，防止错误状态下继续处理其他输入
      this.inputBuffer = [];
      
      // 减少错误状态的显示时间，然后进入下一个字符
      setTimeout(() => {
        this.isWrong = false;

        // 检查是否是最后一个字符
        if (this.currentIndex === this.targetLetters.length - 1) {
          this.generateReport();
          return;
        }

        // 进入下一个字符
        this.currentIndex++;
        this.currentLetter = this.targetLetters[this.currentIndex];
      }, 1000); // 保持800ms的错误显示时间，让用户有足够时间看到错误提示
    },

    generateReport() {
      // 清空输入缓冲区
      this.inputBuffer = [];
      this.isProcessingInput = false;
      window.removeEventListener("keydown", this.handleKeyPress);

      const endTime = new Date();
      this.totalTime = (endTime - this.startTime) / 1000;
      this.typingSpeed = Math.round(
        this.correctAttempts / (this.totalTime / 60)
      );

      if (this.score >= 400 && this.accuracy >= 90) {
        this.resultMessage = "太棒了！你已经完全掌握了基础按键！";
        this.resultImage = this.resultImages.excellent;
      } else if (this.score >= 300 && this.accuracy >= 80) {
        this.resultMessage = "做得不错！继续练习会更好！";
        this.resultImage = this.resultImages.good;
      } else {
        this.resultMessage = "慢慢来，相信你会做得更好！";
        this.resultImage = this.resultImages.keepTrying;
      }

      this.showReport = true;
      this.completeLevelAndSave();
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}分${remainingSeconds}秒`;
    },

    completeLevelAndSave() {
      const completedLevels = JSON.parse(
        localStorage.getItem("completedLevels") || "[]"
      );
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId);
        localStorage.setItem(
          "completedLevels",
          JSON.stringify(completedLevels)
        );
      }
    },

    restartGame() {
      this.score = 0;
      this.totalAttempts = 0;
      this.correctAttempts = 0;
      this.accuracy = 100;
      this.progress = 0;
      this.showReport = false;
      this.errorIndices = []; // 清空错误索引
      this.lastWrongKey = ""; // 清空最后错误键
      this.startTime = new Date();
      this.generateChallenge();
      window.addEventListener("keydown", this.handleKeyPress);
    },

    restartPractice() {
      this.restartGame();
    },

    backToMap() {
      this.$router.go(-1);
    },

    toggleStats() {
      this.isStatsCollapsed = !this.isStatsCollapsed;
    },

    exitPractice() {
      this.gameStarted = false;
      this.showGuide = true;
      this.showReport = false;
      this.score = 0;
      this.totalAttempts = 0;
      this.correctAttempts = 0;
      this.accuracy = 100;
      this.progress = 0;
      window.removeEventListener("keydown", this.handleKeyPress);
      this.$router.go(-1);
    },

    updateAccuracy() {
      this.accuracy = Math.round(
        (this.correctAttempts / this.totalAttempts) * 100
      );
    },

    closeReport() {
      this.showReport = false;
      this.$router.go(-1);
    },

    goToNextLevel() {
      this.showReport = false;
      this.$router.push(this.nextLevel);
    },

    parseRouteParams() {
      // 检查完整的路由信息
      console.log("Route:", this.$route);

      // 尝试从 query 和 params 中获取参数
      const letters = this.$route.query.letters || this.$route.params.letters;
      const length = this.$route.query.length || this.$route.params.length;
      const title = this.$route.query.title || this.$route.params.title;
      const levelId = this.$route.query.levelId || this.$route.params.levelId;
      const next = this.$route.query.next || this.$route.params.next;

      if (letters) {
        try {
          // 直接将字符串转换为字符数组
          this.practiceKeys = letters.split("");
          console.log("Practice keys:", this.practiceKeys);
        } catch (e) {
          console.error("无效的键位参数:", e);
        }
      }

      if (length) {
        const parsedLength = parseInt(length);
        if (!isNaN(parsedLength) && parsedLength > 0) {
          this.practiceLength = parsedLength;
        }
      }

      if (title) {
        this.title = title;
      }

      if (levelId) {
        this.levelId = levelId;
      }

      if (next) {
        this.nextLevel = next;
      }

      // 确保在参数解析后立即启动游戏
      this.startGame();
    },

    updateKeyboardGuide() {
      // 这里可以根据practiceKeys动态生成键盘指导
      // 在本次修改中我们保持简单实现
    },
  },
  mounted() {
    this.parseRouteParams();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyPress);
  },
};
</script>

<style scoped>
/* 整体布局 */
.typing-game {
  max-height: 100vh;
  margin: 20px;
  position: relative;
  display: flex;
}

/* 主要内容区域 */
.game-main {
  flex: 1;
  margin-right: var(--sidebar-width, 280px);
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto;
  padding: 0 20px 0 0;
}

/* 游戏显示区域 */
.game-display {
  background: white;
  padding: 30px;
  border-radius: 20px;
  margin-bottom: 30px;
  background: white;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
    border: 2px solid rgba(25, 118, 210, 0.1);
}

.game-display.correct {
  background: #e8f5e9;
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.3);
  animation: correctFlash 0.15s ease;
}

.game-display.wrong {
  background: #ffebee;
  border: 2px solid #f44336;
  box-shadow: 0 0 15px rgba(244, 67, 54, 0.3);
  animation: wrongFlash 0.15s ease;
}

@keyframes correctFlash {
  0% {
    background: white;
  }
  50% {
    background: #e8f5e9;
  }
  100% {
    background: white;
  }
}

@keyframes wrongFlash {
  0% {
    background: white;
  }
  50% {
    background: #ffebee;
  }
  100% {
    background: white;
  }
}

.target-letters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin: 0 0 20px 0;
  padding: 20px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 20px;
}

.target-letters span {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  transition: all 0.15s ease;
  position: relative;
}

/* 当前需要输入的字符样式 */
.target-letters span.current {
  background: #2196f3;
  color: white;
  border-color: #1976d2;
  transform: scale(1.1);
  box-shadow: 0 8px 15px rgba(33, 150, 243, 0.3);
  z-index: 2;
  animation: currentPulse 1s infinite;
}

@keyframes currentPulse {
  0% {
    transform: scale(1.1);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1.1);
  }
}

.target-letters span.current::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  background: #2196f3;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

/* 已完成的字符样式 */
.target-letters span.completed {
  background: #e8f5e9;
  color: #4caf50;
  border-color: #4caf50;
  transform: scale(0.95);
  opacity: 0.8;
  transition: all 0.1s ease;
}

/* 错误标记的字符样式 */
.target-letters span.error-marked {
  background: #ffebee;
  color: #f44336;
  border-color: #f44336;
  transform: scale(0.95);
  opacity: 0.8;
  text-decoration: line-through;
  transition: all 0.1s ease;
}

/* 未完成的字符样式 */
.target-letters span.upcoming {
  background: #f5f5f5;
  color: #9e9e9e;
  border-color: #e0e0e0;
  transform: scale(0.9);
  opacity: 0.6;
}

/* 空格键特殊样式 */
.target-letters span.space {
  width: 80px;
  background: #f5f5f5;
  border-style: dashed;
  font-size: 16px;
}

.target-letters span.space.current {
  background: #2196f3;
  border-style: solid;
  color: white;
}

.target-letters span.space.completed {
  background: #e8f5e9;
  border-style: solid;
  border-color: #4caf50;
  color: #4caf50;
}

/* 错误反馈样式 */
.target-letters span.error {
  background: #ffebee;
  color: #f44336;
  border-color: #f44336;
  animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }
  30%,
  50%,
  70% {
    transform: translate3d(-2px, 0, 0);
  }
  40%,
  60% {
    transform: translate3d(2px, 0, 0);
  }
}

.error-feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #f44336;
  background: #ffebee;
  padding: 10px 15px;
  border-radius: 8px;
  margin-top: 10px;
  animation: fadeIn 0.2s ease;
}

.error-icon {
  font-size: 20px;
  animation: pulse 0.5s infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 进度条容器 */
.progress-container {
  background: white;
  padding: 20px;
  border-radius: 15px;
}

.progress {
  height: 10px;
  background: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #2196f3, #4caf50);
  border-radius: 5px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  animation: progressGlow 2s linear infinite;
}

.progress-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  font-size: 14px;
  color: #666;
}

.progress-value {
  font-weight: bold;
  color: #2196f3;
}

/* 键盘显示区域 */
.practice-keyboard {
  background: #2c3e50;
  padding: 20px;
  border-radius: 12px;
  margin: 25px auto;
  width: fit-content;
  transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid #1e2b38;
}

.practice-row {
  display: flex;
  gap: 5px;
  margin-bottom: 5px;
  position: relative;
}

/* 错开排列，模拟真实键盘 */
.practice-row:nth-child(2) {
  padding-left: 12px;
}

.practice-row:nth-child(3) {
  padding-left: 18px;
}

.practice-row:nth-child(4) {
  padding-left: 25px;
}

.practice-key {
  width: 40px;
  height: 40px;
  background: #34495e;
  color: white;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 3px 0 #1e2b38;
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
}

.practice-key::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0)
  );
  border-radius: 5px 5px 0 0;
}

.practice-key.active {
  background: #2196f3;
  transform: translateY(3px);
  box-shadow: 0 0 0 #1e2b38;
}

/* 特殊键位的宽度 */
.backspace-key {
  width: 70px;
}

.tab-key {
  width: 60px;
}

.backslash-key {
  width: 50px;
}

.caps-key {
  width: 70px;
}

.enter-key {
  width: 75px;
}

.shift-key {
  width: 90px;
}

.shift-key-right {
  width: 90px;
}

.ctrl-key,
.alt-key,
.win-key,
.menu-key {
  width: 50px;
}

.space-key {
  width: 250px;
  font-size: 14px;
}

/* 键盘指导区域 */
.typing-guide {
  margin-top: 20px;
  padding: 30px;
  border-radius: 20px;
  background: white;
}

.guide-keyboard {
  background: #263238;
  padding: 30px;
  border-radius: 15px;
  position: relative;
  margin: 0 auto;
  max-width: 800px;
}

.guide-row {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin: 5px 0;
  position: relative;
}

/* 偏移量设置，模拟标准键盘布局 */
.top-row {
  padding-left: 0;
}

.home-row {
  padding-left: 20px; /* 相对顶行有偏移 */
}

.bottom-row {
  padding-left: 40px; /* 相对中行更多偏移 */
}

.guide-key {
  width: 50px;
  height: 50px;
  background: #37474f;
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  position: relative;
  margin: 3px;
  box-shadow: 0 4px 0 #1e272c;
  user-select: none;
}

.guide-key.active {
  background: #2196f3;
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e272c;
  animation: keyGlow 0.5s ease;
}

@keyframes keyGlow {
  0% {
    box-shadow: 0 0 0 #1e272c, 0 0 0 rgba(33, 150, 243, 0);
  }
  50% {
    box-shadow: 0 0 0 #1e272c, 0 0 20px rgba(33, 150, 243, 0.8);
  }
  100% {
    box-shadow: 0 0 0 #1e272c, 0 0 0 rgba(33, 150, 243, 0);
  }
}

.guide-key.error-key {
  background: #f44336;
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e272c;
  animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both,
    errorGlow 0.5s ease;
}

@keyframes errorGlow {
  0% {
    box-shadow: 0 0 0 #1e272c, 0 0 0 rgba(244, 67, 54, 0);
  }
  50% {
    box-shadow: 0 0 0 #1e272c, 0 0 20px rgba(244, 67, 54, 0.8);
  }
  100% {
    box-shadow: 0 0 0 #1e272c, 0 0 0 rgba(244, 67, 54, 0);
  }
}

.guide-key.hidden {
  visibility: hidden;
  opacity: 0.2;
}

.guide-key.space-key {
  width: 250px;
  height: 40px;
  background: #455a64;
  font-size: 16px;
  margin-top: 10px;
}

.guide-key.space-key.active {
  background: #2196f3;
  transform: translateY(4px);
  box-shadow: 0 0 0 #1e272c;
}

.home-row .guide-key[data-finger="LF"] {
  border-left: 3px solid #ff9800;
}

.home-row .guide-key[data-finger="RF"] {
  border-right: 3px solid #ff9800;
}

/* 动画效果 */
@keyframes progressGlow {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 侧边栏样式 */
.game-sidebar {
  --sidebar-width: 220px;
  position: fixed;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  width: var(--sidebar-width);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.8),
    rgba(240, 249, 255, 0.9)
  );
  border-radius: 20px 0 0 20px;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.05),
    -2px 0 5px rgba(33, 150, 243, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  z-index: 100;
  border-left: 2px solid rgba(255, 255, 255, 0.8);
  padding-right: 5px;
}

.game-sidebar::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
      circle at 10% 10%,
      rgba(33, 150, 243, 0.05) 0%,
      transparent 60%
    ),
    radial-gradient(
      circle at 90% 90%,
      rgba(76, 175, 80, 0.05) 0%,
      transparent 60%
    );
  z-index: -1;
  opacity: 0.6;
  animation: bgShift 15s infinite alternate ease-in-out;
}

@keyframes bgShift {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(5deg);
  }
}

.game-sidebar.collapsed {
  width: 30px;
  background: rgba(255, 255, 255, 0.95);
  height: 100vh;
  top: 0;
  transform: translateY(0);
  border-radius: 0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
}

.collapse-toggle {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(
    to right,
    rgba(33, 150, 243, 0.2),
    rgba(33, 150, 243, 0.1)
  );
  border-radius: 0 8px 8px 0;
  transition: all 0.3s ease;
  z-index: 101;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
}

.collapse-toggle::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  border-radius: 0 8px 8px 0;
  opacity: 0.5;
}

.collapse-toggle:hover {
  background: linear-gradient(
    to right,
    rgba(33, 150, 243, 0.3),
    rgba(33, 150, 243, 0.2)
  );
  width: 35px;
}

.toggle-icon {
  font-size: 14px;
  color: #2196f3;
  transition: all 0.3s ease;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
  z-index: 2;
}

.collapse-toggle:hover .toggle-icon {
  transform: scale(1.2);
}

.collapsed .toggle-icon {
  transform: rotate(180deg);
}

.stats-container {
  padding: 20px 15px 15px 35px;
  opacity: 1;
  transform: translateX(0);
  transition: all 0.3s ease;
}

.collapsed .stats-container {
  opacity: 0;
  transform: translateX(100%);
}

.sidebar-header {
  text-align: center;
  padding-bottom: 15px;
  position: relative;
}

.sidebar-header h1 {
  font-size: 22px;
  background: linear-gradient(45deg, #2196f3, #03a9f4);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;
}

.sidebar-header h1::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(to right, #2196f3, #03a9f4);
  border-radius: 3px;
}

.divider {
  height: 2px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(33, 150, 243, 0.5),
    transparent
  );
  margin: 15px 0;
  opacity: 0.5;
  position: relative;
  overflow: hidden;
}

.divider::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(200%);
  }
}

.stats-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stat-box {
  position: relative;
  padding: 12px 15px;
  border-radius: 15px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03), 0 1px 3px rgba(0, 0, 0, 0.02),
    inset 0 1px 1px rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.7);
}

.stat-box:hover {
  transform: translateX(-8px) translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05), 0 3px 5px rgba(0, 0, 0, 0.03),
    inset 0 1px 1px rgba(255, 255, 255, 0.9);
}

.stat-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2),
    rgba(255, 255, 255, 0.4)
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.stat-box:hover::before {
  opacity: 1;
}

.stat-box.score {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(230, 255, 230, 0.7)
  );
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.stat-box.accuracy {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(230, 240, 255, 0.7)
  );
  border-left: 3px solid rgba(33, 150, 243, 0.7);
}

.stat-box.speed {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7),
    rgba(255, 240, 230, 0.7)
  );
  border-left: 3px solid rgba(255, 152, 0, 0.7);
}

.stat-icon {
  width: 40px;
  height: 40px;
  font-size: 20px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  animation: iconFloat 3s infinite ease-in-out;
}

@keyframes iconFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

.score .stat-icon {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: white;
}

.accuracy .stat-icon {
  background: linear-gradient(135deg, #2196f3, #03a9f4);
  color: white;
}

.speed .stat-icon {
  background: linear-gradient(135deg, #ff9800, #ffc107);
  color: white;
}

.stat-icon::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
}

.stat-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stat-content .stat-value {
  font-size: 26px;
  font-weight: 700;
  white-space: nowrap;
  text-align: center;
  margin-bottom: 2px;
  background: linear-gradient(45deg, #333, #555);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  letter-spacing: 0.5px;
  position: relative;
}

.score .stat-value {
  background: linear-gradient(45deg, #2e7d32, #43a047);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.accuracy .stat-value {
  background: linear-gradient(45deg, #1565c0, #1976d2);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.speed .stat-value {
  background: linear-gradient(45deg, #e65100, #f57c00);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-content .stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.8;
  text-align: center;
  font-weight: 500;
  color: #666;
}

.stat-content .stat-label::before {
  display: none;
}

.stat-label {
  font-size: 15px;
  color: #666;
  position: relative;
  display: inline-block;
  font-weight: 600;
}

.stat-label::before {
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

/* 退出按钮样式 */
.exit-button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #ff5252, #ff1744);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(255, 82, 82, 0.2),
    inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  margin-top: 5px;
}

.exit-button::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
}

.exit-button:hover {
  background: linear-gradient(135deg, #ff1744, #d50000);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(255, 82, 82, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.2);
}

.exit-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(255, 82, 82, 0.2),
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

.button-icon {
  font-size: 16px;
  position: relative;
  z-index: 2;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .game-sidebar {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.9),
      rgba(25, 42, 60, 0.95)
    );
    border-left: 2px solid rgba(255, 255, 255, 0.1);
  }

  .game-sidebar::before {
    background: radial-gradient(
        circle at 10% 10%,
        rgba(33, 150, 243, 0.1) 0%,
        transparent 60%
      ),
      radial-gradient(
        circle at 90% 90%,
        rgba(76, 175, 80, 0.1) 0%,
        transparent 60%
      );
  }

  .game-sidebar.collapsed {
    background: rgba(25, 42, 60, 0.95);
  }

  .collapse-toggle {
    background: linear-gradient(
      to right,
      rgba(33, 150, 243, 0.3),
      rgba(33, 150, 243, 0.2)
    );
  }

  .collapse-toggle:hover {
    background: linear-gradient(
      to right,
      rgba(33, 150, 243, 0.4),
      rgba(33, 150, 243, 0.3)
    );
  }

  .sidebar-header h1 {
    background: linear-gradient(45deg, #64b5f6, #42a5f5);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .divider {
    background: linear-gradient(
      to right,
      transparent,
      rgba(33, 150, 243, 0.3),
      transparent
    );
  }

  .stat-box {
    background: rgba(44, 62, 80, 0.7);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1),
      inset 0 1px 1px rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.05);
  }

  .stat-box.score {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(38, 60, 38, 0.7)
    );
  }

  .stat-box.accuracy {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(38, 50, 60, 0.7)
    );
  }

  .stat-box.speed {
    background: linear-gradient(
      135deg,
      rgba(44, 62, 80, 0.7),
      rgba(60, 50, 38, 0.7)
    );
  }

  .stat-icon {
    background: rgba(25, 42, 60, 0.9);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  }

  .stat-content .stat-value {
    background: linear-gradient(45deg, #b0bec5, #90a4ae);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .score .stat-value {
    background: linear-gradient(45deg, #81c784, #a5d6a7);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .accuracy .stat-value {
    background: linear-gradient(45deg, #64b5f6, #90caf9);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .speed .stat-value {
    background: linear-gradient(45deg, #ffb74d, #ffcc80);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .stat-content .stat-label {
    color: #b0bec5;
  }
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .typing-game {
    flex-direction: column;
  }

  .game-main {
    margin-right: 0;
    padding: 10px;
  }

  .game-sidebar {
    position: static;
    width: 100%;
    transform: none;
    border-radius: 16px;
    margin-bottom: 20px;
  }

  .collapse-toggle {
    display: none;
  }

  .stats-container {
    padding: 15px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .stat-box {
    margin: 0;
  }

  .divider {
    display: none;
  }

  .exit-button {
    grid-column: 1 / -1;
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .typing-game {
    background: linear-gradient(135deg, #1a1c20 0%, #2c3e50 100%);
  }

  .game-main > div {
    background: #2c3e50;
    color: #eceff1;
  }

  .target-letters span {
    background: #34495e;
    border-color: #455a64;
    color: #eceff1;
  }

  .game-sidebar {
    background: rgba(44, 62, 80, 0.75);
    border-left: 1px solid rgba(255, 255, 255, 0.05);
  }

  .stat-box {
    background: #34495e;
  }

  .stat-value {
    color: #64b5f6;
  }

  .stat-label {
    color: #b0bec5;
  }

  .sidebar-header h1 {
    color: #64b5f6;
  }

  .collapse-toggle {
    background: rgba(33, 150, 243, 0.3);
  }

  .toggle-icon {
    color: #64b5f6;
  }
}

/* 引导页面样式 */
.guide-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.guide-content {
  background: white;
  border-radius: 25px;
  max-width: 800px;
  width: 90%;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.5s ease-out;
}

.guide-header {
  text-align: center;
  margin-bottom: 30px;
}

.guide-header h2 {
  font-size: 32px;
  color: #2196f3;
  margin: 0;
}

.guide-subtitle {
  color: #666;
  font-size: 18px;
  margin-top: 10px;
}

.guide-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 30px;
}

.guide-section {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 15px;
  transition: transform 0.3s ease;
}

.guide-section:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.guide-section h3 {
  color: #2196f3;
  margin-top: 0;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.guide-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.guide-section li {
  padding: 8px 0;
  color: #666;
  display: flex;
  align-items: center;
  gap: 10px;
}

.guide-section li::before {
  content: "✓";
  color: #4caf50;
  font-weight: bold;
}

.keyboard-preview {
  grid-column: 1 / -1;
  margin-top: 30px;
}

.preview-row {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin: 10px 0;
}

.preview-key {
  width: 60px;
  height: 60px;
  background: #37474f;
  color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  box-shadow: 0 4px 0 #1e272c;
  transition: all 0.2s ease;
  animation: keyPulse 2s infinite alternate;
}

.preview-key.space {
  width: 200px;
}

.guide-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.start-button,
.back-button {
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.start-button {
  background: #4caf50;
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.start-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.back-button {
  background: #e9ecef;
  color: #495057;
}

.back-button:hover {
  background: #dee2e6;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .guide-content {
    background: #1a1c20;
    color: #eceff1;
  }

  .guide-section {
    background: #2c3e50;
  }

  .guide-section li {
    color: #b0bec5;
  }

  .guide-subtitle {
    color: #b0bec5;
  }

  .back-button {
    background: #34495e;
    color: #eceff1;
  }

  .back-button:hover {
    background: #2c3e50;
  }
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.report-content {
  background: #ffffff;
  padding: 0;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  position: relative;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  70% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.report-content h2 {
  font-size: 28px;
  margin: 0;
  padding: 25px 0;
  color: #ffffff;
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.report-content h2::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.stat-item:nth-child(1) {
  animation-delay: 0.1s;
}
.stat-item:nth-child(2) {
  animation-delay: 0.2s;
}
.stat-item:nth-child(3) {
  animation-delay: 0.3s;
}
.stat-item:nth-child(4) {
  animation-delay: 0.4s;
}

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #e0e7ff;
  background: #f0f7ff;
}

.stat-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4a6fff, #9c4aff);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4a6fff;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.keyword-summary {
  margin: 0 30px 30px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
}

.keyword-summary h3 {
  color: #4a6fff;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 700;
}

.keyword-summary p {
  margin: 0;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.keyword-summary .highlight {
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  background-clip: text;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 700;
  padding: 0 5px;
  position: relative;
}

.keyword-summary .highlight::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4a6fff, #9c4aff);
  opacity: 0.2;
  border-radius: 3px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: "";
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4a6fff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: "";
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9c4aff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  animation: pulse 2s infinite ease-in-out;
  transform-origin: center;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.report-message p {
  font-size: 20px;
  color: #4a6fff;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 0;
  position: relative;
  display: block;
  text-align: center;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  color: white;
}

.report-buttons button:first-child:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(74, 111, 255, 0.3);
}

.report-buttons button:first-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(74, 111, 255, 0.2);
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4a6fff;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

.keyboard-hint {
  text-align: center;
  margin-top: 20px;
  font-size: 16px;
  color: #455a64;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px;
  border-radius: 8px;
}

.keyboard-hint strong {
  color: #2196f3;
  font-size: 18px;
  background: rgba(33, 150, 243, 0.1);
  padding: 3px 8px;
  border-radius: 4px;
  margin: 0 5px;
}

@media (prefers-color-scheme: dark) {
  .keyboard-hint {
    color: #b0bec5;
    background: rgba(0, 0, 0, 0.2);
  }

  .keyboard-hint strong {
    background: rgba(33, 150, 243, 0.2);
    color: #64b5f6;
  }
}

/* 新增的指令样式 */
.letter-instruction {
  margin: 20px auto;
  transition: all 0.3s ease;
}

.instruction-bubble {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-radius: 20px;
  padding: 15px 20px;
  box-shadow: 0 8px 15px rgba(33, 150, 243, 0.15);
  border: 2px solid #90caf9;
  position: relative;
  overflow: hidden;
  animation: float 3s infinite ease-in-out;
  max-width: 500px;
  margin: 0 auto;
}

.instruction-bubble::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  height: 50%;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  z-index: 0;
}

.instruction-bubble::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 20px;
  background: #90caf9;
  border-radius: 50%;
  filter: blur(10px);
  opacity: 0.7;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.instruction-icon {
  font-size: 28px;
  margin-right: 15px;
  position: relative;
  z-index: 1;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

.instruction-text {
  font-size: 20px;
  font-weight: 700;
  color: #1565c0;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.error-feedback {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #ffebee, #ffcdd2);
  border-radius: 20px;
  padding: 15px 20px;
  box-shadow: 0 8px 15px rgba(244, 67, 54, 0.15);
  border: 2px solid #ef9a9a;
  position: relative;
  overflow: hidden;
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
  max-width: 500px;
  margin: 0 auto;
}

.error-feedback::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  height: 50%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  z-index: 0;
}

@keyframes shake {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }
  30%, 50%, 70% {
    transform: translate3d(-3px, 0, 0);
  }
  40%, 60% {
    transform: translate3d(3px, 0, 0);
  }
}

.error-icon {
  font-size: 28px;
  margin-right: 15px;
  position: relative;
  z-index: 1;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.error-message {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.error-title {
  font-size: 18px;
  font-weight: 700;
  color: #c62828;
  margin-bottom: 5px;
}

.error-detail {
  font-size: 16px;
  color: #d32f2f;
}

.key-highlight {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 8px;
  font-weight: 700;
  margin: 0 3px;
}

.key-highlight.correct {
  background-color: rgba(76, 175, 80, 0.2);
  color: #2e7d32;
  border: 1px dashed #4caf50;
}

.key-highlight.wrong {
  background-color: rgba(244, 67, 54, 0.2);
  color: #c62828;
  border: 1px dashed #f44336;
  text-decoration: line-through;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .instruction-bubble {
    background: linear-gradient(135deg, #1a237e, #283593);
    border-color: #3949ab;
  }
  
  .instruction-bubble::before {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .instruction-text {
    color: #90caf9;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }
  
  .error-feedback {
    background: linear-gradient(135deg, #b71c1c, #c62828);
    border-color: #d32f2f;
  }
  
  .error-feedback::before {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .error-title {
    color: #ffcdd2;
  }
  
  .error-detail {
    color: #ef9a9a;
  }
  
  .key-highlight.correct {
    background-color: rgba(76, 175, 80, 0.3);
    color: #a5d6a7;
    border-color: #81c784;
  }
  
  .key-highlight.wrong {
    background-color: rgba(244, 67, 54, 0.3);
    color: #ef9a9a;
    border-color: #e57373;
  }
}
</style>
